import { useState, useEffect, useCallback } from 'react';
import { getIvrQuestions, updateIvrQuestion } from '@services/api';
import { parseApiError } from '@utils/errorUtils';
import './IvrQuestionsTab.css';

// ── Default form state ────────────────────────────────────────────────────────

const DEFAULT_EDIT = { textEN: '', textES: '', rejectionEN: '' };

// ── QuestionRow ───────────────────────────────────────────────────────────────
// Edits the GLOBAL default wording for an IVR question. Per-company on/off and
// per-company verbiage overrides live on the "IVR Flow" tab (company customizer).

function QuestionRow({ question, onSaved }) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(DEFAULT_EDIT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const openEdit = () => {
    setForm({
      textEN: question.textEN ?? '',
      textES: question.textES ?? '',
      rejectionEN: question.rejectionEN ?? '',
    });
    setEditMode(true);
    setError('');
  };

  const cancelEdit = () => {
    setEditMode(false);
    setError('');
  };

  const saveEdit = async () => {
    if (!form.textEN.trim()) { setError('English text is required.'); return; }
    try {
      setSaving(true);
      setError('');
      await updateIvrQuestion(question.id, {
        textEN: form.textEN.trim(),
        textES: form.textES.trim() || null,
        rejectionEN: form.rejectionEN.trim() || null,
      });
      setSuccess('Saved.');
      setEditMode(false);
      onSaved();
    } catch (err) {
      setError('Failed to save: ' + parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="iq-question-card">
      {/* Question header */}
      <div className="iq-question-header">
        <div className="iq-question-meta">
          <span className="iq-identifier">{question.identifier}</span>
          {question.formLocation && (
            <span className="iq-location">{question.formLocation}</span>
          )}
          <span className="iq-badge gather">{question.gatherDigits} digit{question.gatherDigits !== 1 ? 's' : ''}</span>
          {question.overrideCount > 0 && (
            <span className="iq-badge override-count">{question.overrideCount} company override{question.overrideCount !== 1 ? 's' : ''}</span>
          )}
        </div>
        <button className="btn-edit-small" onClick={openEdit} disabled={editMode}>Edit</button>
      </div>

      {/* Current text (read mode) */}
      {!editMode && (
        <div className="iq-text-display">
          <div className="iq-lang-row">
            <span className="iq-lang-label">EN</span>
            <span className="iq-lang-text">{question.textEN || <em className="iq-empty">Not set — hardcoded fallback will be used</em>}</span>
          </div>
          {question.textES && (
            <div className="iq-lang-row">
              <span className="iq-lang-label">ES</span>
              <span className="iq-lang-text">{question.textES}</span>
            </div>
          )}
          {question.rejectionEN && (
            <div className="iq-lang-row rejection">
              <span className="iq-lang-label">Rejection</span>
              <span className="iq-lang-text">{question.rejectionEN}</span>
            </div>
          )}
        </div>
      )}

      {success && <div className="iq-success">{success}</div>}
      {error && <div className="iq-error">{error}</div>}

      {/* Edit form */}
      {editMode && (
        <div className="iq-edit-form">
          <div className="iq-field">
            <label>English Prompt</label>
            <textarea
              value={form.textEN}
              onChange={(e) => setForm((p) => ({ ...p, textEN: e.target.value }))}
              rows={3}
              placeholder="English IVR prompt text"
            />
          </div>
          <div className="iq-field">
            <label>Spanish Prompt <span className="field-hint">(optional)</span></label>
            <textarea
              value={form.textES}
              onChange={(e) => setForm((p) => ({ ...p, textES: e.target.value }))}
              rows={2}
              placeholder="Spanish IVR prompt text (optional)"
            />
          </div>
          <div className="iq-field">
            <label>Rejection Response <span className="field-hint">(optional — spoken on invalid input)</span></label>
            <textarea
              value={form.rejectionEN}
              onChange={(e) => setForm((p) => ({ ...p, rejectionEN: e.target.value }))}
              rows={2}
              placeholder="Text spoken when patient enters invalid input (optional)"
            />
          </div>
          <div className="iq-form-actions">
            <button className="btn-save" onClick={saveEdit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn-cancel" onClick={cancelEdit}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

export default function IvrQuestionsTab() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setQuestions(await getIvrQuestions());
    } catch (err) {
      setError('Failed to load IVR questions: ' + parseApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  if (loading) return (
    <div className="ivr-questions-tab">
      <h2>IVR Question Wording</h2>
      <p className="iq-loading">Loading questions…</p>
    </div>
  );

  return (
    <div className="ivr-questions-tab">
      <div className="iq-tab-header">
        <div>
          <h2>IVR Question Wording (Global Defaults)</h2>
          <p className="iq-tab-desc">
            Edit the global default prompt text spoken during IVR calls. Changes take effect within
            5 minutes (cache TTL). To turn a question on/off or override its wording for a specific
            company, use the company customizer on the <strong>IVR Flow</strong> tab.
          </p>
        </div>
      </div>

      {error && <div className="iq-error">{error}</div>}

      {questions.length === 0 && !error && (
        <p className="iq-empty">No IVR questions found. Ensure questions are seeded with <code>bUsedInIVR = 1</code>.</p>
      )}

      <div className="iq-questions-list">
        {questions.map((q) => (
          <QuestionRow key={q.id} question={q} onSaved={fetchQuestions} />
        ))}
      </div>
    </div>
  );
}
